import { useCallback, useRef } from 'react';

import isEqual from 'fast-deep-equal';

import type { Noop } from '@enterprise/core';

import type { PaginationContextReturnValue } from '#pagination/context/types/context-return-value.type';
import type { PaginationContextState } from '#pagination/context/types/context.type';

/**
 * React hook that provides a simple in-memory store for pagination state,
 * along with subscription capabilities for change notifications.
 *
 * The store maintains the current pagination state and allows components to:
 * - Retrieve the current state (`get`)
 * - Update the state (`set`)
 * - Subscribe to state changes (`subscribe`)
 *
 * State updates are shallow-merged and only trigger notifications if the new state
 * is different from the previous state (using deep equality check).
 *
 * @returns {PaginationContextReturnValue} An object containing `get`, `set`, and
 * `subscribe` methods for managing pagination state.
 */
export const usePaginationStore = (): PaginationContextReturnValue => {
  const store = useRef<PaginationContextState>({
    lastClickedPage: null,
    page: 1,
    pageSize: 10,
    total: 0,
  });
  const subscribers = useRef(new Set<Noop>());

  const get = useCallback(() => {
    return store.current;
  }, []);
  const set = useCallback((value: Partial<PaginationContextState>) => {
    const updated = { ...store.current, ...value };

    if (isEqual(store.current, updated)) {
      return;
    }

    store.current = updated;
    for (const callback of subscribers.current) {
      callback();
    }
  }, []);
  const subscribe = useCallback((callback: Noop) => {
    subscribers.current.add(callback);

    return () => {
      subscribers.current.delete(callback);
    };
  }, []);

  return {
    get,
    set,
    subscribe,
  };
};
